import { get as getUser } from 'js/lib/user';

import { areDailyGoalsEnabled, areMmmCelebrationScreensEnabled } from 'bundles/daily-goals/utils/epicFeatureFlags';
import { IdTypes, createFeatureScope } from 'bundles/product-features';

const dailyGoalsFeatureScope = createFeatureScope({
  globalCourseFeatures: {
    enableDailyGoals: true, // value is unused
  },
});

export const useDailyGoalsProductFeature = ({
  shouldSkipQuery = false,
}: {
  shouldSkipQuery?: boolean;
}): { enabled: boolean; loading: boolean } => {
  const { features, loading } = dailyGoalsFeatureScope.useProductFeatures({
    idType: IdTypes.User,
    id: String(getUser().id),
    skip: shouldSkipQuery,
  });
  return {
    // Before loading, the default value is `true` based on the generated types
    // coming from the product features configuration repo.
    // So we need to return `false` before loading is complete to avoid the case where we start out
    // by returning `true` and later return `false` when loading is complete.
    enabled: loading ? false : features.get('globalCourseFeatures', 'enableDailyGoals'),
    loading,
  };
};

export const useIsDailyGoalsFeatureEnabled = (): {
  isDailyGoalsFeatureEnabled: boolean;
  loadingDailyGoalsFeature: boolean;
} => {
  const isEnabledByEpic = areDailyGoalsEnabled(); // EPIC check is temporary until we roll out to GA
  const { enabled: isDailyGoalsFeatureEnabled, loading: loadingDailyGoalsFeature } = useDailyGoalsProductFeature({
    shouldSkipQuery: !isEnabledByEpic,
  });
  return { isDailyGoalsFeatureEnabled: isEnabledByEpic && isDailyGoalsFeatureEnabled, loadingDailyGoalsFeature };
};

// We are launching the MMMM variant that includes celebration screens,
// so once we have rolled out to GA, we can remove this separate check and
// instead use the useIsDailyGoalsFeatureEnabled hook directly.
export const useIsMMMCelebrationScreensEnabled = (): {
  isMMMCelebrationScreensEnabled: boolean;
  loadingMMMCelebrationScreensFeature: boolean;
} => {
  const isEnabledByEpic = areMmmCelebrationScreensEnabled();
  const { enabled: isMMMCelebrationScreensEnabled, loading: loadingMMMCelebrationScreensFeature } =
    useDailyGoalsProductFeature({
      shouldSkipQuery: !isEnabledByEpic,
    });
  return {
    isMMMCelebrationScreensEnabled: isEnabledByEpic && isMMMCelebrationScreensEnabled,
    loadingMMMCelebrationScreensFeature,
  };
};
